import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Calendar,
  Trophy,
  Building2,
  BarChart3,
  Bell,
  Settings,
  LogOut,
  Users,
  Target,
  ShieldCheck,
  UserPlus,
  MessageSquare,
  User,
  Mail,
  UserPlus2,
  LayoutGrid
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/tournaments', label: 'Tournaments', icon: Trophy },
  { path: '/my-tournaments', label: 'My Events', icon: Target },
  { path: '/organizations', label: 'My Organizations', icon: Building2 },
  { path: '/results', label: 'Results', icon: BarChart3 },
  { path: '/analytics', label: 'AI Analytics', icon: BarChart3 },
  { path: '/notifications', label: 'Notifications', icon: Bell },
  { path: '/contact', label: 'Contact & Feedback', icon: MessageSquare },
];

const coachItems = [
  { path: '/club-events', label: 'Club Events', icon: Users },
];

const parentItems = [
  { path: '/family-dashboard', label: 'Family Dashboard', icon: LayoutGrid },
  { path: '/family-archers', label: 'Family Archers', icon: UserPlus2 },
];

const adminItems = [
  { path: '/admin/access-requests', label: 'Access Requests', icon: UserPlus },
  { path: '/admin/users', label: 'Manage Users', icon: Users },
  { path: '/admin/emails', label: 'Authorized Emails', icon: ShieldCheck },
  { path: '/admin/compose-email', label: 'Email Composer', icon: Mail },
];

export const Sidebar = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const isCoach = user?.role === 'coach';
  const isParent = user?.role === 'parent';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="w-64 bg-slate-50 border-r border-slate-200 h-screen sticky top-0 flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900 text-lg leading-tight">Archery Manager</h1>
            <p className="text-xs text-slate-500 font-medium">AI-Powered Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="label-overline px-4 mb-3">Main Menu</p>
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn('sidebar-link', isActive && 'sidebar-link-active')
            }
            data-testid={`nav-${item.path.replace('/', '')}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* Coach-specific menu */}
        {isCoach && (
          <>
            <div className="pt-4 pb-2">
              <p className="label-overline px-4 mb-3">Coach</p>
            </div>
            {coachItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'sidebar-link-active')
                }
                data-testid={`nav-coach-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {/* Parent-specific menu */}
        {isParent && (
          <>
            <div className="pt-4 pb-2">
              <p className="label-overline px-4 mb-3">Family</p>
            </div>
            {parentItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'sidebar-link-active')
                }
                data-testid={`nav-parent-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              <p className="label-overline px-4 mb-3">Admin</p>
            </div>
            {adminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'sidebar-link-active')
                }
                data-testid={`nav-admin-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {/* Profile link */}
        <div className="pt-4 pb-2">
          <p className="label-overline px-4 mb-3">Account</p>
        </div>
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn('sidebar-link', isActive && 'sidebar-link-active')
          }
          data-testid="nav-profile"
        >
          <User className="w-5 h-5" />
          <span>My Profile</span>
        </NavLink>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-primary">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
